import { useState, useCallback, useEffect, useRef } from 'react';
import {
  computeFrameDimensions,
  computeBaseScale,
  computeMinZoom,
  clampPan,
  zoomPanToSourceCrop,
  sourceCropToZoomPan,
} from '../utils/cropMath';
import { CROP_ASPECT } from '../utils/constants';

/**
 * Parse an aspect ratio string like "3:2" into a numeric value (1.5).
 */
function parseAspectRatio(str) {
  if (!str || typeof str !== 'string') return CROP_ASPECT;
  const parts = str.split(':');
  if (parts.length !== 2) return CROP_ASPECT;
  const w = parseFloat(parts[0]);
  const h = parseFloat(parts[1]);
  if (!w || !h || isNaN(w) || isNaN(h)) return CROP_ASPECT;
  return w / h;
}

/**
 * Determine the best matching aspect index for a saved crop.
 */
function findBestAspectIndex(cropXYXY, aspects) {
  if (!cropXYXY || !aspects || aspects.length === 0) return 0;
  const [x1, y1, x2, y2] = cropXYXY;
  const cropAspect = (x2 - x1) / (y2 - y1);
  let bestIdx = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < aspects.length; i++) {
    const a = parseAspectRatio(aspects[i]);
    const diff = Math.abs(a - cropAspect);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  return bestIdx;
}

const DEFAULT_ASPECTS = ['3:2', '2:3'];

/**
 * Custom hook for the zoom/pan crop editor.
 *
 * Instead of resizing/dragging an overlay box, the user zooms and pans the
 * image underneath a fixed aspect-ratio frame. What's visible in the frame
 * IS the final crop -- direct WYSIWYG feedback.
 *
 * @param {Object} options
 * @param {Object} options.imgBounds - Current image bounds {x, y, w, h, nW, nH}
 * @param {Object} options.metaDoc - Photo metadata document
 * @param {Object} options.cropBox - Computed crop box from metadata
 * @param {string} options.selectedImage - Current selected image ID
 * @param {string[]} options.availableAspects - Array of ratio strings like ["3:2", "2:3", "9:16"]
 * @returns {Object} Crop editor state and handlers
 */
export default function useCropEditor({ imgBounds, metaDoc, cropBox, selectedImage, availableAspects }) {
  const aspects = availableAspects && availableAspects.length > 0 ? availableAspects : DEFAULT_ASPECTS;

  // Core crop state
  const [cropMode, setCropMode] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [aspectIndex, setAspectIndex] = useState(0);
  const [cropRotation, setCropRotation] = useState(0);
  const [pendingCrop, setPendingCrop] = useState(null);
  const [userHasModified, setUserHasModified] = useState(false);

  // Drag state for panning
  const [dragState, setDragState] = useState(null);

  // Initialization tracking refs
  const cropInitializedRef = useRef(false);
  const cropEnteredTimeRef = useRef(0);
  const prevCropBoxRef = useRef(null);

  // Derived values
  const currentAspect = parseAspectRatio(aspects[aspectIndex]);
  const isPortrait = currentAspect < 1;
  const currentAspectLabel = aspects[aspectIndex] || '3:2';

  // Refs for stale-closure-safe access in event handlers
  const zoomRef = useRef(zoom);
  const panXRef = useRef(panX);
  const panYRef = useRef(panY);
  const aspectIndexRef = useRef(aspectIndex);
  const currentAspectRef = useRef(currentAspect);
  const cropRotationRef = useRef(cropRotation);
  const aspectsRef = useRef(aspects);
  useEffect(() => {
    zoomRef.current = zoom;
    panXRef.current = panX;
    panYRef.current = panY;
    aspectIndexRef.current = aspectIndex;
    currentAspectRef.current = currentAspect;
    cropRotationRef.current = cropRotation;
    aspectsRef.current = aspects;
  }, [zoom, panX, panY, aspectIndex, currentAspect, cropRotation, aspects]);

  // Derived values: frame dimensions and baseScale
  const getFrameAndBase = useCallback(() => {
    if (!imgBounds) return null;
    const containerW = imgBounds.w || 800;
    const containerH = imgBounds.h || 600;
    const nW = imgBounds.nW || containerW;
    const nH = imgBounds.nH || containerH;

    const { frameW, frameH } = computeFrameDimensions(containerW, containerH, currentAspect);
    const baseScale = computeBaseScale(frameW, frameH, nW, nH);
    return { frameW, frameH, baseScale, containerW, containerH, nW, nH };
  }, [imgBounds, currentAspect]);

  // Get source dimensions for coordinate conversion
  const getSourceDims = useCallback(() => {
    const sati = metaDoc?.pipeline?.sati;
    const sourceW = cropBox?.imgW || sati?.original_dims?.width || imgBounds?.nW || 1;
    const sourceH = cropBox?.imgH || sati?.original_dims?.height || imgBounds?.nH || 1;
    return { sourceW, sourceH };
  }, [metaDoc, cropBox, imgBounds]);

  // Exit crop mode
  const exitCropMode = useCallback(() => {
    setCropMode(false);
    setDragState(null);
  }, []);

  // Convert current zoom/pan to XYXY source-space crop
  const getFullFrameCrop = useCallback(() => {
    const fb = getFrameAndBase();
    if (!fb) return null;
    const { sourceW, sourceH } = getSourceDims();
    return zoomPanToSourceCrop(
      zoomRef.current, panXRef.current, panYRef.current,
      fb.baseScale, fb.frameW, fb.frameH,
      fb.nW, fb.nH, sourceW, sourceH
    );
  }, [getFrameAndBase, getSourceDims]);

  // Clamp zoom and pan to valid range
  const clampZoomPan = useCallback((z, px, py, aspect = currentAspectRef.current, rotation = cropRotationRef.current) => {
    const fb = (() => {
      if (!imgBounds) return null;
      const containerW = imgBounds.w || 800;
      const containerH = imgBounds.h || 600;
      const nW = imgBounds.nW || containerW;
      const nH = imgBounds.nH || containerH;
      const { frameW, frameH } = computeFrameDimensions(containerW, containerH, aspect);
      const baseScale = computeBaseScale(frameW, frameH, nW, nH);
      return { frameW, frameH, baseScale, nW, nH };
    })();
    if (!fb) return { zoom: z, panX: px, panY: py };

    const minZ = computeMinZoom(fb.frameW, fb.frameH, fb.nW, fb.nH, rotation);
    const maxZ = 10;
    const clampedZoom = Math.max(minZ, Math.min(maxZ, z));
    const clamped = clampPan(px, py, clampedZoom, fb.baseScale, fb.frameW, fb.frameH, fb.nW, fb.nH, rotation);
    return { zoom: clampedZoom, panX: clamped.panX, panY: clamped.panY };
  }, [imgBounds]);

  // Apply clamped zoom/pan state
  const applyZoomPan = useCallback((z, px, py, aspect, rotation) => {
    const clamped = clampZoomPan(z, px, py, aspect, rotation);
    setZoom(clamped.zoom);
    setPanX(clamped.panX);
    setPanY(clamped.panY);
  }, [clampZoomPan]);

  // Handle scroll wheel zoom -- slower, consistent steps
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const fb = getFrameAndBase();
    if (!fb) return;

    // Normalize to single tick regardless of momentum
    const ticks = Math.sign(e.deltaY);
    const zoomFactor = ticks < 0 ? 1.03 : 1 / 1.03;
    const oldZoom = zoomRef.current;
    const newZoom = oldZoom * zoomFactor;

    // Zoom toward cursor position within the frame
    const rect = e.currentTarget.getBoundingClientRect();
    const frameCenterX = rect.left + rect.width / 2;
    const frameCenterY = rect.top + rect.height / 2;
    const cursorOffX = e.clientX - frameCenterX;
    const cursorOffY = e.clientY - frameCenterY;

    // Adjust pan so the point under the cursor stays fixed
    const newPanX = cursorOffX - (cursorOffX - panXRef.current) * (newZoom / oldZoom);
    const newPanY = cursorOffY - (cursorOffY - panYRef.current) * (newZoom / oldZoom);

    applyZoomPan(newZoom, newPanX, newPanY);
    setUserHasModified(true);
  }, [getFrameAndBase, applyZoomPan]);

  // Start pan drag
  const startDrag = useCallback((type, e) => {
    if (type !== 'move' && type !== 'pan') return;
    e.preventDefault();
    setDragState({
      type: 'pan',
      startX: e.clientX,
      startY: e.clientY,
      startPanX: panXRef.current,
      startPanY: panYRef.current,
    });
    setUserHasModified(true);
  }, []);

  // Start resize via handle drag -- cursor distance-from-center maps to zoom
  const startHandleDrag = useCallback((handle, e) => {
    const fb = getFrameAndBase();
    if (!fb) return;
    const frameEl = e.target.closest('[class*="frameWrapper"]') || e.target.parentElement;
    const rect = frameEl.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const startDist = Math.hypot(e.clientX - centerX, e.clientY - centerY);
    setDragState({
      type: 'resize',
      handle,
      startZoom: zoomRef.current,
      centerX,
      centerY,
      startDist,
    });
    setUserHasModified(true);
  }, [getFrameAndBase]);

  // Start rotation via zone drag -- cursor angle maps to cropRotation
  const startRotationDrag = useCallback((zone, e) => {
    const frameEl = e.target.closest('[class*="frameWrapper"]') || e.target.parentElement;
    const rect = frameEl.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const startAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180 / Math.PI;
    setDragState({
      type: 'rotate',
      zone,
      startRotation: cropRotationRef.current,
      centerX,
      centerY,
      startAngle,
    });
    setUserHasModified(true);
  }, []);

  // Handle drag events (pan, resize, rotate)
  useEffect(() => {
    if (!dragState) return;

    const cursorMap = { pan: 'grabbing', resize: 'nw-resize', rotate: 'crosshair' };
    document.body.style.cursor = cursorMap[dragState.type] || 'grabbing';

    const onMove = (e) => {
      if (dragState.type === 'resize') {
        const dist = Math.hypot(e.clientX - dragState.centerX, e.clientY - dragState.centerY);
        const ratio = dragState.startDist / Math.max(dist, 1);
        const newZoom = dragState.startZoom * ratio;
        applyZoomPan(newZoom, panXRef.current, panYRef.current);
      } else if (dragState.type === 'rotate') {
        const angle = Math.atan2(e.clientY - dragState.centerY, e.clientX - dragState.centerX) * 180 / Math.PI;
        const delta = angle - dragState.startAngle;
        const newRotation = Math.max(-45, Math.min(45, dragState.startRotation + delta));
        setCropRotation(newRotation);
      } else {
        // Pan
        const dx = e.clientX - dragState.startX;
        const dy = e.clientY - dragState.startY;
        const newPanX = dragState.startPanX + dx;
        const newPanY = dragState.startPanY + dy;
        applyZoomPan(zoomRef.current, newPanX, newPanY);
      }
    };

    const onUp = () => setDragState(null);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragState, applyZoomPan]);

  // Programmatic zoom (for +/- buttons)
  const adjustZoom = useCallback((factor) => {
    const newZoom = zoomRef.current * factor;
    applyZoomPan(newZoom, panXRef.current, panYRef.current);
    setUserHasModified(true);
  }, [applyZoomPan]);

  // Nudge pan with arrow keys
  const nudgePan = useCallback((delta) => {
    const step = 10; // pixels
    const newPanX = panXRef.current + (delta.x || 0) * step;
    const newPanY = panYRef.current + (delta.y || 0) * step;
    applyZoomPan(zoomRef.current, newPanX, newPanY);
    setUserHasModified(true);
  }, [applyZoomPan]);

  // Cycle through available aspect ratios
  const cycleAspectRatio = useCallback(() => {
    if (!imgBounds) return;
    const curIdx = aspectIndexRef.current;
    const allAspects = aspectsRef.current;
    const newIdx = (curIdx + 1) % allAspects.length;
    const newAspect = parseAspectRatio(allAspects[newIdx]);
    setAspectIndex(newIdx);

    // Keep the same center: re-derive zoom/pan for new frame dimensions
    const fb = (() => {
      const containerW = imgBounds.w || 800;
      const containerH = imgBounds.h || 600;
      const nW = imgBounds.nW || containerW;
      const nH = imgBounds.nH || containerH;
      const { frameW, frameH } = computeFrameDimensions(containerW, containerH, currentAspectRef.current);
      const baseScale = computeBaseScale(frameW, frameH, nW, nH);
      return { frameW, frameH, baseScale, containerW, containerH, nW, nH };
    })();
    if (!fb) return;

    const { sourceW, sourceH } = getSourceDims();
    const currentCrop = zoomPanToSourceCrop(
      zoomRef.current, panXRef.current, panYRef.current,
      fb.baseScale, fb.frameW, fb.frameH,
      fb.nW, fb.nH, sourceW, sourceH
    );

    // Compute new crop with new aspect ratio centered on the same point
    const [cx1, cy1, cx2, cy2] = currentCrop;
    const centerX = (cx1 + cx2) / 2;
    const centerY = (cy1 + cy2) / 2;
    const oldW = cx2 - cx1;
    const oldH = cy2 - cy1;

    const refSize = Math.sqrt(oldW * oldH);
    let newCropW = refSize * Math.sqrt(newAspect);
    let newCropH = newCropW / newAspect;

    // Ensure it fits within source bounds
    if (newCropW > sourceW) { newCropW = sourceW; newCropH = newCropW / newAspect; }
    if (newCropH > sourceH) { newCropH = sourceH; newCropW = newCropH * newAspect; }

    const nx1 = Math.max(0, Math.min(sourceW - newCropW, centerX - newCropW / 2));
    const ny1 = Math.max(0, Math.min(sourceH - newCropH, centerY - newCropH / 2));
    const nx2 = nx1 + newCropW;
    const ny2 = ny1 + newCropH;

    // Compute zoom/pan for the new frame
    const containerW = imgBounds.w || 800;
    const containerH = imgBounds.h || 600;
    const nW = imgBounds.nW || containerW;
    const nH = imgBounds.nH || containerH;
    const { frameW: newFrameW, frameH: newFrameH } = computeFrameDimensions(containerW, containerH, newAspect);
    const newBase = computeBaseScale(newFrameW, newFrameH, nW, nH);
    const init = sourceCropToZoomPan([nx1, ny1, nx2, ny2], sourceW, sourceH, nW, nH, newBase, newFrameW, newFrameH);

    applyZoomPan(init.zoom, init.panX, init.panY, newAspect);
    setUserHasModified(true);
  }, [imgBounds, getSourceDims, applyZoomPan]);

  // Alias for backwards compatibility
  const flipCropOrientation = cycleAspectRatio;

  // Set full-frame crop (maximum zoom out)
  const setFullFrameCrop = useCallback(() => {
    if (!imgBounds) return;
    applyZoomPan(1.0, 0, 0);
    setUserHasModified(true);
  }, [imgBounds, applyZoomPan]);

  // Apply crop alternative from composition-aware cropping
  const applyCropAlternative = useCallback((alt) => {
    if (!alt?.box || !imgBounds) return;

    // Parse box to array
    let x1, y1, x2, y2;
    if (Array.isArray(alt.box)) {
      if (alt.box.length !== 4) return;
      [x1, y1, x2, y2] = alt.box;
    } else if (typeof alt.box === 'object') {
      x1 = alt.box.x1; y1 = alt.box.y1; x2 = alt.box.x2; y2 = alt.box.y2;
    } else {
      return;
    }

    if ([x1, y1, x2, y2].some((v) => typeof v !== 'number' || isNaN(v))) return;
    if (x2 <= x1 || y2 <= y1) return;

    const cropXYXY = [x1, y1, x2, y2];

    // Find best matching aspect
    const bestIdx = findBestAspectIndex(cropXYXY, aspectsRef.current);
    setAspectIndex(bestIdx);
    const matchedAspect = parseAspectRatio(aspectsRef.current[bestIdx]);

    const containerW = imgBounds.w || 800;
    const containerH = imgBounds.h || 600;
    const nW = imgBounds.nW || containerW;
    const nH = imgBounds.nH || containerH;
    const { sourceW, sourceH } = getSourceDims();

    const { frameW, frameH } = computeFrameDimensions(containerW, containerH, matchedAspect);
    const baseScale = computeBaseScale(frameW, frameH, nW, nH);
    const init = sourceCropToZoomPan(cropXYXY, sourceW, sourceH, nW, nH, baseScale, frameW, frameH);

    applyZoomPan(init.zoom, init.panX, init.panY, matchedAspect);
    setUserHasModified(true);
  }, [imgBounds, getSourceDims, applyZoomPan]);

  // Reset crop to full frame (zoom out to show entire image)
  const handleResetCrop = useCallback(() => {
    if (!imgBounds) return;
    const aspect = parseAspectRatio(aspectsRef.current[aspectIndexRef.current]);
    applyZoomPan(1.0, 0, 0, aspect, 0);
    setCropRotation(0);
    setPendingCrop(null);
    setUserHasModified(true);
  }, [imgBounds, applyZoomPan]);

  // Wrapper: set rotation AND mark as user-modified
  const setRotationByUser = useCallback((v) => {
    const newRot = typeof v === 'function' ? v(cropRotationRef.current) : v;
    setCropRotation(newRot);
    setUserHasModified(true);
  }, []);

  // Initialize crop state when entering crop mode
  useEffect(() => {
    if (!cropMode) {
      cropInitializedRef.current = false;
      prevCropBoxRef.current = null;
      return;
    }
    if (!imgBounds || !metaDoc) return;

    // Skip re-initialization if user has manually adjusted the crop AND the
    // underlying crop box hasn't genuinely changed (e.g. face propagation
    // refreshes metaDoc but doesn't alter the crop coordinates).
    if (cropInitializedRef.current) {
      if (userHasModified) {
        const prev = prevCropBoxRef.current;
        const same = prev && cropBox &&
          prev.x1 === cropBox.x1 && prev.y1 === cropBox.y1 &&
          prev.x2 === cropBox.x2 && prev.y2 === cropBox.y2;
        if (same || (!prev && !cropBox)) return;
      } else {
        return;
      }
    }

    cropEnteredTimeRef.current = Date.now();

    const containerW = imgBounds.w || 800;
    const containerH = imgBounds.h || 600;
    const nW = imgBounds.nW || containerW;
    const nH = imgBounds.nH || containerH;
    const sati = metaDoc?.pipeline?.sati;
    const sourceW = cropBox?.imgW || sati?.original_dims?.width || nW;
    const sourceH = cropBox?.imgH || sati?.original_dims?.height || nH;

    const cropBoxArray = cropBox ? [cropBox.x1, cropBox.y1, cropBox.x2, cropBox.y2] : null;
    const cropSource = pendingCrop?.crop || cropBoxArray;

    let initAspectIdx;
    let initZoom = 1.0;
    let initPanX = 0;
    let initPanY = 0;

    if (cropSource) {
      // Use the exact crop aspect ratio from the server crop (not a fitted preset)
      // so that crop mode entry matches the yellow dotted line and the actual output.
      const [cx1, cy1, cx2, cy2] = cropSource;
      const cropW = cx2 - cx1;
      const cropH = cy2 - cy1;
      const rawAspect = cropW / cropH;

      // Find the closest preset for the UI label, but use raw aspect for framing
      initAspectIdx = findBestAspectIndex(cropSource, aspects);
      const { frameW, frameH } = computeFrameDimensions(containerW, containerH, rawAspect);
      const baseScale = computeBaseScale(frameW, frameH, nW, nH);
      const init = sourceCropToZoomPan(cropSource, sourceW, sourceH, nW, nH, baseScale, frameW, frameH);
      initZoom = init.zoom;
      initPanX = init.panX;
      initPanY = init.panY;
    } else {
      // No saved crop -- default to first aspect, full frame
      initAspectIdx = 0;
    }

    // Restore rotation
    const rollApplied = metaDoc?.roll_info?.applied;
    const originalTilt = rollApplied ? metaDoc?.roll_info?.angle_deg : 0;
    const metaTilt =
      pendingCrop?.rotation ?? metaDoc?.pipeline?.recrop?.rotation ?? originalTilt ?? 0;
    const initRotation = metaTilt || 0;

    // Update refs EAGERLY so later effects in this cycle (rotation re-clamp,
    // imgBounds re-clamp) read the correct values instead of stale zoom=1/pan=0.
    zoomRef.current = initZoom;
    panXRef.current = initPanX;
    panYRef.current = initPanY;
    aspectIndexRef.current = initAspectIdx;
    currentAspectRef.current = parseAspectRatio(aspects[initAspectIdx]);
    cropRotationRef.current = initRotation;

    setAspectIndex(initAspectIdx);
    setZoom(initZoom);
    setPanX(initPanX);
    setPanY(initPanY);
    setCropRotation(initRotation);
    setUserHasModified(false);

    prevCropBoxRef.current = cropBox ? { x1: cropBox.x1, y1: cropBox.y1, x2: cropBox.x2, y2: cropBox.y2 } : null;
    cropInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropMode, cropBox, pendingCrop, metaDoc, imgBounds]);

  // Reset when image changes
  useEffect(() => {
    setCropMode(false);
    setZoom(1);
    setPanX(0);
    setPanY(0);
    setAspectIndex(0);
    setPendingCrop(null);
    setCropRotation(0);
    setUserHasModified(false);
    cropInitializedRef.current = false;
  }, [selectedImage]);

  // Re-clamp when rotation changes
  useEffect(() => {
    if (!cropMode || !imgBounds) return;
    applyZoomPan(zoomRef.current, panXRef.current, panYRef.current, currentAspectRef.current, cropRotation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropRotation, cropMode, applyZoomPan]);

  // Re-clamp when container resizes (imgBounds change)
  // Also re-initialize if imgBounds changed significantly shortly after entering crop mode
  useEffect(() => {
    if (!cropMode || !imgBounds) return;
    const timeSinceEntry = Date.now() - cropEnteredTimeRef.current;
    if (timeSinceEntry < 300 && cropInitializedRef.current) {
      // imgBounds likely shifted due to layout; re-run full initialization
      cropInitializedRef.current = false;
      return; // The init effect above will pick up the change
    }
    applyZoomPan(zoomRef.current, panXRef.current, panYRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgBounds, cropMode]);

  // Legacy compatibility: generate cropOverlay-like object for any code that reads it
  const cropOverlay = cropMode ? {
    w: currentAspectLabel.split(':').map(Number)[0] || 3,
    h: currentAspectLabel.split(':').map(Number)[1] || 2,
  } : null;

  return {
    // State
    cropMode,
    setCropMode,
    cropOverlay,
    cropRotation,
    setCropRotation,
    setRotationByUser,
    pendingCrop,
    setPendingCrop,
    dragState,
    userHasModified,
    zoom,
    panX,
    panY,
    isPortrait,
    currentAspectLabel,

    // Handlers
    exitCropMode,
    flipCropOrientation,
    cycleAspectRatio,
    setFullFrameCrop,
    applyCropAlternative,
    nudgePan,
    adjustZoom,
    handleResetCrop,
    startDrag,
    startHandleDrag,
    startRotationDrag,
    handleWheel,

    // Utilities
    getFullFrameCrop,
    getFrameAndBase,
    getSourceDims,
  };
}
